import React, { createContext, useContext, useMemo } from "react";
import { backendUrl } from "../../api/constants";
import Chip from "../kit/Chip";

// Create a stable context specifically for tag filter rendering
const StableTagContext = createContext();

// Stable provider that only updates when knownServices actually changes
export const StableTagProvider = ({ knownServices, children }) => {
  // Create completely stable service URLs
  const stableServiceData = useMemo(() => {
    const services = knownServices || {};
    const serviceUrls = {};
    const filteredServices = [];
    
    Object.entries(services).forEach(([id, service]) => {
      if (service.filterActive !== false) {
        serviceUrls[id] = `${backendUrl}/${service.icon}`;
        filteredServices.push([id, service]);
      }
    });
    
    return {
      serviceUrls,
      filteredServices,
      servicesCount: Object.keys(services).length
    };
  }, [knownServices]);
  
  return (
    <StableTagContext.Provider value={stableServiceData}>
      {children}
    </StableTagContext.Provider>
  );
};

// Hook to use stable tag data
export const useStableTagData = () => {
  const context = useContext(StableTagContext);
  if (!context) {
    throw new Error('useStableTagData must be used within StableTagProvider');
  }
  return context;
};

// Completely isolated tag image component that never re-renders unless its specific URL changes
export const StableTagImage = React.memo(({ serviceId, serviceName }) => {
  const { serviceUrls } = useStableTagData();
  const url = serviceUrls[serviceId];
  
  return (
    <div className="w-4 h-4 rounded-kit-full flex items-center justify-center overflow-hidden shrink-0">
      <img 
        src={url} 
        alt={serviceName} 
        className="w-full h-full object-contain"
        style={{
          imageRendering: 'crisp-edges'
        }}
      />
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison - only re-render if serviceId or serviceName actually changed
  return prevProps.serviceId === nextProps.serviceId && 
         prevProps.serviceName === nextProps.serviceName;
});

// Completely isolated tag filter component
export const StableTagFilter = React.memo(({ 
  serviceId, 
  serviceName, 
  isActive, 
  onClick 
}) => {
  return (
    <Chip
      selected={isActive}
      icon={<StableTagImage serviceId={serviceId} serviceName={serviceName} />}
      onClick={() => onClick(serviceId)}
    >
      {serviceName}
    </Chip>
  );
}, (prevProps, nextProps) => {
  // Only re-render if the props that actually matter changed
  return prevProps.serviceId === nextProps.serviceId &&
         prevProps.serviceName === nextProps.serviceName &&
         prevProps.isActive === nextProps.isActive;
  // Deliberately exclude onClick from comparison since it's always stable
});

// Completely isolated tag list component
export const StableTagList = React.memo(({ activeTags, onTagClick }) => {
  const { filteredServices } = useStableTagData();
  
  return (
    <div className="flex flex-wrap gap-kit-8 justify-center items-center">
      {filteredServices.map(([id, service]) => (
        <StableTagFilter
          key={id}
          serviceId={id}
          serviceName={service.name}
          isActive={activeTags.includes(id)}
          onClick={onTagClick}
        />
      ))}
    </div>
  );
}, (prevProps, nextProps) => {
  // Only re-render if activeTags actually changed
  return JSON.stringify(prevProps.activeTags) === JSON.stringify(nextProps.activeTags);
  // onClick is stable, so we don't compare it
});
